import React, { useState, useEffect } from "react";

import { useWsNavigate } from "../hooks/useWsNavigate";
import { scenariosAPI, aiTestingAgentAPI } from "../services/api";
import PageHeader from "../components/layout/PageHeader";
import { useAuth } from "../contexts/AuthContext";
import { useAgent } from "../contexts/AgentContext";
import { usePageTitle } from "../contexts/PageTitleContext";
import AgentPicker from "../components/AgentPicker";
import { CheckSquare, Square, Tag, Phone, Globe, MessageSquare } from "lucide-react";
import { useCan } from "../hooks/useCan";
import { SearchInput } from "../components/ui/search-input";
import { usePostHog } from "@posthog/react";

const NewBatchRun = () => {
  const navigate = useWsNavigate();
  const { currentWorkspace } = useAuth();
  const { agents, selectedAgent, setSelectedAgent } = useAgent();
  const { setPageTitle, setParentPage } = usePageTitle();
  const canWriteBatch = useCan("batch", "write");
  const canStartStopBatch = useCan("start_stop_rerun_batch", "write");
  const posthog = usePostHog();

  const [localAgent, setLocalAgent] = useState(selectedAgent || null);
  const [name, setName] = useState("");
  const [testingMode, setTestingMode] = useState("voice");
  // channel: "phone" | "web" | "text" — the new three-way channel selector.
  // voiceCallMode is retained for dual-write compatibility with the backend.
  const [channel, setChannel] = useState("phone");
  const [voiceCallMode, setVoiceCallMode] = useState("phone");
  const [scenarios, setScenarios] = useState([]);
  const [selectedScenarios, setSelectedScenarios] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [agentConfig, setAgentConfig] = useState(null);

  useEffect(() => {
    setPageTitle("New Batch Run");
    setParentPage({ name: "Batch", href: "/batch-run" });
    return () => { setPageTitle(null); setParentPage(null); };
  }, [setPageTitle, setParentPage]);

  // Fetch scenarios when agent changes
  useEffect(() => {
    if (!localAgent?.id || !currentWorkspace?.id) {
      setScenarios([]);
      setSelectedScenarios([]);
      return;
    }
    const fetchScenarios = async () => {
      try {
        setLoading(true);
        setSelectedScenarios([]);
        const data = await scenariosAPI.getScenariosByAgent(localAgent?.id, currentWorkspace.id);
        setScenarios(Array.isArray(data) ? data : data?.data || []);
      } catch (err) {
        setError("Failed to load scenarios.");
      } finally {
        setLoading(false);
      }
    };
    fetchScenarios();
  }, [localAgent?.id, currentWorkspace]);

  // Fetch agent config when agent changes
  useEffect(() => {
    if (!localAgent?.id || !currentWorkspace?.id) { setAgentConfig(null); return; }
    aiTestingAgentAPI.getAgent(localAgent?.id, currentWorkspace?.id)
      .then((res) => {
        const a = res?.data || res;
        // Derive which channels are configured on the agent. Prefer new
        // channel jsonb; fall back to legacy fields for agents not yet
        // migrated.
        const legacyWebTypes = ["livekit_managed", "daily_managed", "websocket", "pipecat_livekit", "external_vapi", "external_retell"];
        const phoneConfigured =
          !!a?.phone_channel?.provider ||
          !!a?.phone_number ||
          !!a?.twilio_phone_number ||
          ["pipecat_twilio", "vapi"].includes(a?.integration_type);
        const webConfigured =
          !!a?.web_channel?.provider ||
          !!a?.web_call_url ||
          legacyWebTypes.includes(a?.integration_type);
        const textConfigured =
          !!a?.text_channel?.provider ||
          !!a?.text_config?.text_endpoint ||
          !!a?.text_config?.text_websocket_url;

        setAgentConfig({
          phone_number: a?.phone_number,
          web_call_url: a?.web_call_url,
          integration_type: a?.integration_type,
          integration: a?.integration,
          phone_configured: phoneConfigured,
          web_configured: webConfigured,
          text_configured: textConfigured,
        });

        // Default the channel to the first configured channel in priority
        // order (phone → web → text), but only if the currently-selected
        // channel isn't configured. This prevents defaulting to a disabled
        // channel (e.g. a text-only agent should land on "text", not "phone").
        const isConfigured = (c) =>
          (c === "phone" && phoneConfigured) ||
          (c === "web" && webConfigured) ||
          (c === "text" && textConfigured);
        if (!isConfigured(channel)) {
          if (phoneConfigured) {
            setChannel("phone");
            setVoiceCallMode("phone");
          } else if (webConfigured) {
            setChannel("web");
            setVoiceCallMode("web_call");
          } else if (textConfigured) {
            setChannel("text");
          }
        }
      })
      .catch(() => setAgentConfig(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localAgent?.id, currentWorkspace?.id]);

  // Keep testing mode in sync with channel: text channel → text mode;
  // phone/web channels → voice mode.
  useEffect(() => {
    if (channel === "text" && testingMode !== "text") setTestingMode("text");
    if (channel !== "text" && testingMode === "text") setTestingMode("voice");
  }, [channel]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleScenario = (id) => {
    setSelectedScenarios((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
  };

  const toggleAll = () => {
    if (selectedScenarios.length === filteredScenarios.length) {
      setSelectedScenarios([]);
    } else {
      setSelectedScenarios(filteredScenarios.map((s) => s.id));
    }
  };

  const handleCreate = async () => {
    if (!name.trim() || selectedScenarios.length === 0) return;
    setSubmitting(true);
    setError(null);
    try {
      const selectedData = scenarios.filter((s) => selectedScenarios.includes(s.id));

      const batchData = {
        name: name.trim(),
        batch_type: "scenario",
        testing_mode: channel === "text" ? "text" : "voice",
        voice_call_mode: channel === "phone" ? "phone" : "web_call",
        channel,
        agent_id: localAgent?.id,
        client_id: currentWorkspace.id,
        scenarios: JSON.stringify(selectedData),
      };

      const response = await scenariosAPI.createBatch(batchData);
      const batchId = response?.data?.id;
      posthog?.capture('batch_run_created', {
        batch_id: batchId,
        agent_id: localAgent?.id,
        channel,
        scenario_count: selectedScenarios.length,
      });
      navigate(batchId ? `/batch-run/${batchId}` : "/batch-run");
    } catch (err) {
      setError("Failed to create batch. Please try again.");
      setSubmitting(false);
    }
  };

  const filteredScenarios = scenarios.filter((s) => {
    const searchMatch =
      !searchTerm ||
      s.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.instructions?.toLowerCase().includes(searchTerm.toLowerCase());
    return searchMatch;
  });

  const allSelected =
    filteredScenarios.length > 0 &&
    filteredScenarios.every((s) => selectedScenarios.includes(s.id));

  return (
    <div className="px-4">
      <PageHeader
        description="Name your batch and pick the scenarios to run."
        breadcrumbs={[
          { label: "Batch Runs", path: "/batch-run" },
          { label: "New Batch Run", path: "/batch-run/new" },
        ]}
      />

      {/* Agent + Name + Testing Mode */}
      <div className="bg-card rounded-xl border border-border p-4 mb-5">
        <div className="flex items-start gap-6">
          {/* Agent Selector */}
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">
              Agent <span className="text-red-400">*</span>
            </label>
            <AgentPicker
              agents={agents}
              selectedAgent={localAgent}
              setSelectedAgent={setLocalAgent}
              onAdd={() => navigate("/ai-testing-agent/create")}
            />
          </div>

          {/* Batch Name */}
          <div className="flex-1">
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">
              Batch Name <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Sprint 3 — Customer Support Regression"
              className="w-full px-3 py-2.5 border border-input rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
          </div>

        </div>
      </div>

      {/* Channel picker — Phone / Web / Text */}
      {localAgent && (
        <div className="bg-card rounded-xl border border-border p-4 mb-5">
          <label className="block text-xs font-medium text-muted-foreground mb-3">
            Channel
          </label>
          <div className="flex gap-3">
            {[
              { v: "phone", label: "Phone", Icon: Phone, enabled: agentConfig?.phone_configured, activeClass: "border-primary bg-primary/5 text-primary" },
              { v: "web", label: "Web", Icon: Globe, enabled: agentConfig?.web_configured, activeClass: "border-indigo-500 bg-indigo-50 text-indigo-700" },
              { v: "text", label: "Text", Icon: MessageSquare, enabled: agentConfig?.text_configured, activeClass: "border-emerald-500 bg-emerald-50 text-emerald-700" },
            ].map(({ v, label, Icon, enabled, activeClass }) => (
              <button
                key={v}
                type="button"
                onClick={() => {
                  if (!enabled) return;
                  setChannel(v);
                  if (v === "phone") setVoiceCallMode("phone");
                  else if (v === "web") setVoiceCallMode("web_call");
                  setSelectedScenarios([]);
                }}
                disabled={!enabled}
                title={enabled ? undefined : `${label} channel is not configured on this agent`}
                className={`flex items-center gap-2 px-4 py-3 rounded-lg border text-sm font-medium transition-colors flex-1 justify-center
                  ${channel === v
                    ? activeClass
                    : "border-border text-muted-foreground hover:bg-muted"}
                  ${!enabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`}
              >
                <Icon className="h-4 w-4" />
                {label}
                {v === "phone" && enabled && agentConfig?.phone_number && (
                  <span className="text-xs font-mono text-muted-foreground">{agentConfig.phone_number}</span>
                )}
              </button>
            ))}
          </div>
          {agentConfig &&
           !agentConfig.phone_configured &&
           !agentConfig.web_configured &&
           !agentConfig.text_configured && (
            <p className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
              This agent has no channels configured. Edit the agent and enable Phone, Web, or Text.
            </p>
          )}
        </div>
      )}

      {/* Scenario selection */}
      <div className="bg-card rounded-xl border border-border overflow-hidden mb-5">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div className="flex items-center gap-3">
            <h2 className="text-sm font-semibold text-foreground">
              Select Scenarios
              {selectedScenarios.length > 0 && (
                <span className="ml-2 text-xs font-medium text-primary bg-primary/10 px-2 py-0.5 rounded-full">
                  {selectedScenarios.length} selected
                </span>
              )}
            </h2>
          </div>
          <div className="flex items-center gap-3">
            <SearchInput
              placeholder="Search scenarios..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-48"
            />
            {filteredScenarios.length > 0 && (
              <button
                onClick={toggleAll}
                className="text-xs text-primary font-medium hover:underline"
              >
                {allSelected ? "Deselect all" : "Select all"}
              </button>
            )}
          </div>
        </div>

        {!localAgent?.id ? (
          <div className="py-16 text-center">
            <p className="text-sm font-medium text-foreground mb-1">Select an agent first</p>
            <p className="text-sm text-muted-foreground">
              Choose an agent above to see its scenarios.
            </p>
          </div>
        ) : loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900" />
          </div>
        ) : filteredScenarios.length === 0 ? (
          <div className="py-16 text-center">
            <p className="text-sm font-medium text-foreground mb-1">No scenarios found</p>
            <p className="text-sm text-muted-foreground">
              {searchTerm
                ? "No scenarios match your search"
                : `No ${testingMode} scenarios found for this agent. Create scenarios in Live Simulations first.`}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {filteredScenarios.map((scenario) => {
              const selected = selectedScenarios.includes(scenario.id);
              return (
                <div
                  key={scenario.id}
                  onClick={() => toggleScenario(scenario.id)}
                  className={`flex items-start gap-4 px-5 py-4 cursor-pointer transition-colors ${
                    selected ? "bg-primary/5" : "hover:bg-muted/60"
                  }`}
                >
                  <div className="mt-0.5 flex-shrink-0">
                    {selected ? (
                      <CheckSquare className="h-4 w-4 text-primary" />
                    ) : (
                      <Square className="h-4 w-4 text-muted-foreground" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium ${selected ? "text-primary" : "text-foreground"}`}>
                      {scenario.name}
                    </p>
                    {scenario.instructions && (
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                        {scenario.instructions}
                      </p>
                    )}
                    {scenario.metrics?.length > 0 ? (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {scenario.metrics.map((m) => (
                          <span
                            key={m.id}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground"
                          >
                            <Tag className="h-2.5 w-2.5" />
                            {m.name}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-amber-600 mt-1">
                        No metrics selected — nothing will be measured for this scenario
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-end gap-3">
        <button
          onClick={() => navigate("/batch-run")}
          className="px-4 py-2.5 text-sm font-medium text-muted-foreground border border-border rounded-lg hover:bg-muted transition-colors"
        >
          Cancel
        </button>
        {(canWriteBatch || canStartStopBatch) && (
          <button
            onClick={handleCreate}
            disabled={!name.trim() || selectedScenarios.length === 0 || submitting}
            className="px-5 py-2.5 text-sm font-medium rounded-lg text-white bg-primary hover:bg-primary/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {submitting ? "Creating..." : `Create Batch Run${selectedScenarios.length > 0 ? ` (${selectedScenarios.length})` : ""}`}
          </button>
        )}
      </div>
    </div>
  );
};

export default NewBatchRun;
